import { InfoIcon } from 'lucide-react'
import { useState } from 'react'

import { Modal, RadioButton, RadioGroup, TextButton, TextLink } from 'slab'

interface AleQuestionSectionProps {
  aleStatus: boolean | null
  onAleStatusChange: (status: boolean) => void
  showFormTypeHint?: boolean
}

export default function AleQuestionSection ({
  aleStatus,
  onAleStatusChange,
  showFormTypeHint = true
}: AleQuestionSectionProps) {
  const [showAleModal, setShowAleModal] = useState(false)
  const formType = aleStatus === true ? '1095-C' : '1095-B'

  return (
    <>
      <div>
        <label className='block text-sm font-medium text-gray-900 mb-1'>
          Are you an Applicable Large Employer (ALE)?
        </label>

        <RadioGroup name='ale_status'>
          <div className='flex flex-col space-y-2'>
            <RadioButton
              label='Yes'
              value='true'
              checked={aleStatus === true}
              onChange={() => onAleStatusChange(true)}
            />
            <RadioButton
              label='No'
              value='false'
              checked={aleStatus === false}
              onChange={() => onAleStatusChange(false)}
            />
          </div>
        </RadioGroup>

        {showFormTypeHint && (
          <p className='mt-2 text-sm' style={{ color: '#6E6E6E' }}>
            {aleStatus !== null && (
              <span style={{ color: '#6E6E6E' }}>
                As {aleStatus ? 'an ALE' : 'a non-ALE'}, you'll be receiving a {formType} form.{' '}
              </span>
            )}
            <TextButton color='gray' size='small' onClick={() => setShowAleModal(true)}>
              How do I know if I'm an ALE?
            </TextButton>
          </p>
        )}
      </div>

      <Modal open={showAleModal} onOpenChange={setShowAleModal}>
        <Modal.Header>
          <Modal.Title>How do I know if I'm an ALE?</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className='space-y-4'>
            <div>
              <h3 className='font-semibold text-gray-900 mb-2'>Use IRS guidelines</h3>
              <p className='text-sm text-gray-600'>
                Please see the IRS guide for more information:{' '}
                <TextLink
                  color='purple'
                  size='small'
                  href='https://www.irs.gov/affordable-care-act/employers/determining-if-an-employer-is-an-applicable-large-employer'
                  target='_blank'
                  rel='noopener noreferrer'
                >
                  Determining if an Employer is an Applicable Large Employer
                </TextLink>
              </p>
            </div>

            <div>
              <h3 className='font-semibold mb-2' style={{ color: '#303030' }}>Talk to an advisor</h3>
              <p className='text-sm text-gray-600'>
                Please speak to a legal, compliance, or tax advisor if you have questions specific to your business.
              </p>
            </div>

            <div className='bg-yellow-50 border border-yellow-300 rounded-lg p-4'>
              <div className='flex gap-3'>
                <InfoIcon className='text-yellow-500 flex-shrink-0' size={20} />
                <p className='text-sm text-yellow-500'>
                  These resources are to help you understand ACA reporting. They are not designed to determine your filing obligations or Applicable Large Employer (ALE) status. Thatch does not provide legal, tax, or compliance advice and your specific filing requirements depend on your unique circumstances. Please consult your HR team, legal counsel, or tax advisor if you have questions.
                </p>
              </div>
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <div className='flex justify-end'>
            <Modal.Button variant='primary' onClick={() => setShowAleModal(false)}>
              Close
            </Modal.Button>
          </div>
        </Modal.Footer>
      </Modal>
    </>
  )
}
